import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAgency } from '../lib/AgencyContext';
import { digitsOnly, pibError, mbError } from '../lib/validation';
import { ACTIVITY_CODES } from '../lib/helpers';
import Icon from './Icon';

// ============================================================
// UVOZ NALOGA (GLUMACA) IZ EXCELA — šablon FinArt_uvoz_klijenata.xlsx
// Jedan red = jedan nalog sa podacima o firmi (stranica Agencija).
// ============================================================

const H = {
  account: 'Naziv naloga *', company: 'Pun naziv firme *', short: 'Skraćeni naziv', owner: 'Ime i prezime obveznika',
  pib: 'PIB', mb: 'Matični broj', taxCode: 'Šifra obveznika', regDate: 'Datum registracije', activity: 'Šifra delatnosti',
  street: 'Ulica', streetNo: 'Broj', postal: 'Poštanski broj', city: 'Grad', email: 'Email', phone: 'Telefon',
  accDom: 'Dinarski tekući račun', accIno: 'Devizni IBAN', swift: 'SWIFT/BIC', bank: 'Banka (devizni račun)',
};

const norm = (s) => String(s ?? '').toLowerCase().replace(/đ/g, 'dj').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
const str = (v) => (v === null || v === undefined ? '' : String(v).trim());
const pad = (n) => String(n).padStart(2, '0');

function toIsoDate(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const d = new Date(v.getTime() + 12 * 3600 * 1000);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  if (typeof v === 'number') {
    const p = XLSX.SSF.parse_date_code(v);
    return p ? `${p.y}-${pad(p.m)}-${pad(p.d)}` : null;
  }
  const s = str(v);
  const m = s.match(/^(\d{1,2})\s*[./-]\s*(\d{1,2})\s*[./-]\s*(\d{4})\.?$/);
  return m ? `${m[3]}-${pad(m[2])}-${pad(m[1])}` : null;
}

function parseRows(rows, existingNames, existingPibs) {
  const items = [];
  const errors = [];
  const seenNames = new Set();
  rows.forEach((r, idx) => {
    const line = idx + 2;
    const any = Object.values(r).some((v) => str(v) !== '');
    if (!any) return;
    const account = str(r[H.account]);
    if (/^primer/i.test(account)) return;
    const company = str(r[H.company]);
    const pib = digitsOnly(r[H.pib]);
    const mb = digitsOnly(r[H.mb]);
    const label = account || company || `red ${line}`;
    if (!account) errors.push(`Red ${line}: fali naziv naloga.`);
    if (!company) errors.push(`Red ${line} (${label}): fali pun naziv firme.`);
    const pe = pibError(pib); if (pe) errors.push(`Red ${line} (${label}): ${pe}`);
    const me = mbError(mb); if (me) errors.push(`Red ${line} (${label}): ${me}`);
    const code = digitsOnly(r[H.activity]).slice(0, 4);
    const activityName = ACTIVITY_CODES.find((a) => a.code === code)?.name || '';
    const key = norm(account);
    let skip = '';
    if (existingNames.has(key)) skip = 'nalog sa ovim nazivom već postoji';
    else if (pib && existingPibs.has(pib)) skip = 'nalog sa ovim PIB-om već postoji';
    else if (seenNames.has(key)) skip = 'ponavlja se u tabeli';
    seenNames.add(key);
    items.push({
      line, account, skip,
      profile: {
        company_name: company, short_name: str(r[H.short]) || null, name_display: 'full',
        owner_name: str(r[H.owner]), pib, mb, taxpayer_code: str(r[H.taxCode]),
        registration_date: toIsoDate(r[H.regDate]), activity_code: code, activity_name: activityName,
        street: str(r[H.street]), street_number: str(r[H.streetNo]), postal_code: str(r[H.postal]), city: str(r[H.city]),
        email: str(r[H.email]), phone: str(r[H.phone]),
        account_dom: str(r[H.accDom]), account_ino: str(r[H.accIno]).replace(/\s/g, ''), swift: str(r[H.swift]), iban_bank: str(r[H.bank]),
      },
    });
  });
  return { items, errors };
}

export default function ImportAgencies({ onClose }) {
  const { user } = useAuth();
  const { agencies, refreshAgencies } = useAgency();
  const fileRef = useRef(null);
  const [step, setStep] = useState('pick');
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const readFile = async (file) => {
    setError('');
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const ws = wb.Sheets.Klijenti || wb.Sheets[wb.SheetNames.find((n) => n !== 'Uputstvo') || wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
      if (!rows.length || !(H.account in rows[0])) {
        setError('Ovo ne liči na FinArt šablon za naloge (ne vidim kolonu "Naziv naloga *"). Koristite šablon FinArt_uvoz_klijenata.xlsx.');
        return;
      }
      // Postojeći nazivi i PIB-ovi, da se ne prave duplikati
      const existingNames = new Set(agencies.map((a) => norm(a.name)));
      const existingPibs = new Set();
      const ids = agencies.map((a) => a.id);
      if (ids.length) {
        const { data } = await supabase.from('agency_profile').select('pib, agency_id').in('agency_id', ids);
        (data || []).forEach((p) => { const d = digitsOnly(p.pib); if (d) existingPibs.add(d); });
      }
      const p = parseRows(rows, existingNames, existingPibs);
      if (!p.items.length) { setError('U tabeli nema naloga za uvoz.'); return; }
      setParsed(p);
      setFileName(file.name);
      setStep('preview');
    } catch (err) {
      setError(`Fajl nije mogao da se pročita: ${err.message}`);
    }
  };

  const runImport = async () => {
    const todo = parsed.items.filter((it) => !it.skip);
    setStep('importing');
    setProgress({ done: 0, total: todo.length });
    const res = { created: 0, failed: [] };
    for (let i = 0; i < todo.length; i++) {
      const it = todo[i];
      try {
        const { data: ag, error: aErr } = await supabase.from('agencies')
          .insert({ owner_user_id: user.id, name: it.account }).select().single();
        if (aErr) throw new Error(aErr.message);
        const { error: pErr } = await supabase.from('agency_profile')
          .upsert({ agency_id: ag.id, onboarding_complete: true, ...it.profile }, { onConflict: 'agency_id' });
        if (pErr) throw new Error(`nalog je napravljen, ali podaci firme nisu sačuvani: ${pErr.message}`);
        res.created += 1;
      } catch (err) {
        res.failed.push(`${it.account}: ${err.message}`);
      } finally {
        setProgress({ done: i + 1, total: todo.length });
      }
    }
    await refreshAgencies();
    setResult(res);
    setStep('done');
  };

  const toCreate = parsed ? parsed.items.filter((it) => !it.skip) : [];
  const skipped = parsed ? parsed.items.filter((it) => it.skip) : [];

  return (
    <div style={styles.overlay} onClick={() => step !== 'importing' && onClose()}>
      <div style={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div style={styles.head}>
          <div>
            <div style={styles.title}>Uvoz naloga iz Excela</div>
            <div style={styles.sub}>Svaki red u tabeli postaje poseban nalog sa podacima o firmi.</div>
          </div>
          {step !== 'importing' && (
            <button onClick={onClose} style={styles.closeBtn} aria-label="Zatvori"><Icon name="x" size={16} /></button>
          )}
        </div>

        {step === 'pick' && (
          <>
            <div role="button" tabIndex={0} style={styles.drop}
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); readFile(e.dataTransfer.files?.[0]); }}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
              <Icon name="users" size={26} color="var(--lavender-700)" />
              <strong>Izaberi popunjen Excel fajl sa glumcima</strong>
              <span style={styles.muted}>ili ga prevuci ovde</span>
            </div>
            <a href="/FinArt_uvoz_klijenata.xlsx" download style={styles.templateLink}>
              <Icon name="download" size={15} /> Preuzmi prazan šablon za naloge
            </a>
          </>
        )}

        {step === 'preview' && parsed && (
          <>
            <div style={styles.summary}>
              <div>Iz fajla <em>{fileName}</em>: <strong>{toCreate.length}</strong> novih naloga{skipped.length ? `, ${skipped.length} preskočeno` : ''}.</div>
            </div>
            {parsed.errors.length > 0 && (
              <div style={styles.errorBox}>
                <strong>Ispravite u Excelu pre uvoza ({parsed.errors.length}):</strong>
                <ul style={styles.ul}>{parsed.errors.slice(0, 12).map((e) => <li key={e}>{e}</li>)}</ul>
                {parsed.errors.length > 12 && <div>...i još {parsed.errors.length - 12}.</div>}
              </div>
            )}
            <div style={styles.list}>
              {parsed.items.map((it) => (
                <div key={it.line} style={{ ...styles.row, ...(it.skip ? { opacity: 0.55 } : {}) }}>
                  <span style={styles.avatar}>{(it.account || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}</span>
                  <span style={styles.rowMid}>
                    <span style={styles.rowName}>{it.account || '—'}</span>
                    <span style={styles.muted}>{it.profile.company_name}{it.profile.pib ? ` · PIB ${it.profile.pib}` : ''}</span>
                  </span>
                  {it.skip && <span style={styles.skipTag}>{it.skip}</span>}
                </div>
              ))}
            </div>
            <div style={styles.actions}>
              <button onClick={() => { setStep('pick'); setParsed(null); }} style={styles.cancelBtn}>Izaberi drugi fajl</button>
              <button onClick={runImport} disabled={parsed.errors.length > 0 || toCreate.length === 0}
                style={{ ...styles.primaryBtn, opacity: parsed.errors.length || !toCreate.length ? 0.5 : 1 }}>
                <Icon name="check" size={16} strokeWidth={2.2} /> Otvori {toCreate.length} naloga
              </button>
            </div>
          </>
        )}

        {step === 'importing' && (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Otvaram naloge... {progress.done} / {progress.total}</div>
            <div style={styles.track}><div style={{ ...styles.fill, width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} /></div>
            <div style={{ ...styles.muted, marginTop: 10 }}>Ne zatvarajte prozor dok se uvoz ne završi.</div>
          </div>
        )}

        {step === 'done' && result && (
          <>
            <div style={styles.okBox}>Otvoreno naloga: <strong>{result.created}</strong>.</div>
            {result.failed.length > 0 && (
              <div style={styles.errorBox}><strong>Nije uspelo ({result.failed.length}):</strong>
                <ul style={styles.ul}>{result.failed.slice(0, 10).map((s) => <li key={s}>{s}</li>)}</ul></div>
            )}
            <div style={styles.actions}>
              <button onClick={onClose} style={styles.primaryBtn}>Gotovo</button>
            </div>
          </>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,37,48,0.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--surface)', borderRadius: 10, padding: 22, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--ink-900)' },
  sub: { fontSize: 13, color: 'var(--ink-500)', marginTop: 2 },
  closeBtn: { width: 36, height: 36, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-700)' },
  drop: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 150, border: '1.5px dashed var(--lavender-500)', background: 'var(--lavender-100)', borderRadius: 10, padding: 16, textAlign: 'center', cursor: 'pointer', color: 'var(--ink-900)' },
  muted: { fontSize: 12, color: 'var(--ink-500)' },
  templateLink: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 13, fontWeight: 700, color: 'var(--teal-700)', textDecoration: 'none' },
  summary: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', marginBottom: 14 },
  row: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--line)', borderRadius: 10, padding: '8px 12px' },
  avatar: { width: 32, height: 32, borderRadius: 9, background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 },
  rowMid: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  rowName: { fontSize: 14, fontWeight: 700, color: 'var(--ink-900)' },
  skipTag: { fontSize: 11, fontWeight: 700, color: 'var(--gold-600)', background: 'var(--gold-100)', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' },
  cancelBtn: { minHeight: 42, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '0 16px', fontWeight: 600, fontSize: 14, color: 'var(--ink-700)' },
  primaryBtn: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 42, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 10, padding: '0 18px', fontWeight: 700, fontSize: 14 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  okBox: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '12px 14px', fontSize: 14, marginBottom: 12 },
  ul: { margin: '6px 0 0', paddingLeft: 18 },
  track: { height: 10, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 99, overflow: 'hidden' },
  fill: { height: '100%', background: 'var(--lavender-700)', transition: 'width 0.2s' },
};
